<template>
  <Dialog
    header="Level System Anomalies Detector"
    v-model:visible="dialogVisible"
    modal
    :style="{ width: '90vw', maxWidth: '1400px' }"
    :closable="true"
    @hide="$emit('close')"
  >
    <!-- Top Bar -->
    <div class="flex flex-wrap items-center gap-3 mb-4">
      <Button
        label="Scan Users"
        icon="pi pi-search"
        @click="runScan"
        :loading="scanning"
      />
      <Button
        label="Export CSV"
        icon="pi pi-download"
        severity="secondary"
        @click="exportReport"
        :disabled="!scanComplete"
        :loading="exporting"
      />
      <Button
        label="Bulk Repair"
        icon="pi pi-wrench"
        severity="warning"
        @click="showBulkRepairDialog = true"
        :disabled="!scanComplete || anomalousCount === 0"
      />
      <div class="flex items-center gap-2 ml-auto">
        <ToggleSwitch v-model="showOnlyAnomalous" />
        <label class="text-sm text-gray-700">Hide healthy users</label>
      </div>
    </div>

    <!-- Summary Stats -->
    <div v-if="scanComplete" class="grid grid-cols-5 gap-4 mb-4">
      <div class="bg-blue-50 border border-blue-200 rounded-lg p-3 text-center">
        <div class="text-2xl font-bold text-blue-700">{{ totalScanned }}</div>
        <div class="text-sm text-blue-600">Users Scanned</div>
      </div>
      <div class="bg-green-50 border border-green-200 rounded-lg p-3 text-center">
        <div class="text-2xl font-bold text-green-700">{{ healthyCount }}</div>
        <div class="text-sm text-green-600">Healthy</div>
      </div>
      <div
        v-if="criticalCount > 0"
        class="rounded-lg p-3 text-center anomaly-critical-card"
      >
        <div class="text-2xl font-bold">{{ criticalCount }}</div>
        <div class="text-sm font-semibold flex items-center justify-center gap-1">
          <i class="pi pi-exclamation-circle" />
          Critical
        </div>
      </div>
      <div v-else class="bg-gray-50 border border-gray-200 rounded-lg p-3 text-center">
        <div class="text-2xl font-bold text-gray-400">0</div>
        <div class="text-sm text-gray-400">Critical</div>
      </div>
      <div class="bg-red-50 border border-red-200 rounded-lg p-3 text-center">
        <div class="text-2xl font-bold text-red-700">{{ anomalousCount }}</div>
        <div class="text-sm text-red-600">Anomalous</div>
      </div>
      <div class="bg-amber-50 border border-amber-200 rounded-lg p-3 text-center">
        <div class="text-2xl font-bold text-amber-700">{{ hintCount }}</div>
        <div class="text-sm text-amber-600">Hints</div>
      </div>
    </div>

    <!-- Users DataTable -->
    <DataTable
      v-if="scanComplete"
      :value="filteredUsers"
      :paginator="true"
      :rows="25"
      :rowsPerPageOptions="[10, 25, 50, 100]"
      dataKey="userId"
      :expandedRows="expandedRows"
      @rowExpand="onRowExpand"
      paginatorTemplate="FirstPageLink PrevPageLink PageLinks NextPageLink LastPageLink CurrentPageReport RowsPerPageDropdown"
      currentPageReportTemplate="Showing {first} to {last} of {totalRecords} users"
      responsiveLayout="scroll"
      :loading="scanning"
    >
      <Column :expander="true" headerStyle="width: 3rem" />
      <Column field="name" header="User" :sortable="true">
        <template #body="{ data }">
          <div>
            <div class="font-medium">{{ data.name || 'N/A' }}</div>
            <div class="text-xs text-gray-500">{{ data.email }}</div>
          </div>
        </template>
      </Column>
      <Column header="Status" :sortable="true" sortField="status">
        <template #body="{ data }">
          <span
            v-if="data.status === 'critical'"
            class="inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-xs font-bold anomaly-critical-badge"
          >
            <i class="pi pi-exclamation-circle anomaly-critical-icon" />
            CRITICAL
          </span>
          <span
            v-else
            :class="[
              'inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium',
              data.status === 'healthy'
                ? 'bg-green-100 text-green-800'
                : data.status === 'hint'
                  ? 'bg-amber-100 text-amber-800'
                  : 'bg-red-100 text-red-800',
            ]"
          >
            <i :class="data.status === 'healthy' ? 'pi pi-check-circle' : data.status === 'hint' ? 'pi pi-info-circle' : 'pi pi-exclamation-triangle'" />
            {{ data.status === 'healthy' ? 'Healthy' : data.status === 'hint' ? 'Hints Only' : 'Anomalous' }}
          </span>
        </template>
      </Column>
      <Column field="anomalyCount" header="Anomalies" :sortable="true" />
      <Column field="hintCount" header="Hints" :sortable="true" />
      <Column header="Types">
        <template #body="{ data }">
          <div class="flex flex-wrap gap-1">
            <span
              v-for="t in data.anomalyTypes"
              :key="t"
              class="text-xs bg-orange-100 text-orange-800 px-1.5 py-0.5 rounded"
            >
              {{ formatAnomalyType(t) }}
            </span>
          </div>
        </template>
      </Column>

      <!-- Expanded Row -->
      <template #expansion="{ data }">
        <div class="p-4">
          <div v-if="loadingDetails[data.userId]" class="text-center py-8">
            <i class="pi pi-spinner pi-spin text-2xl text-gray-400" />
            <div class="text-sm text-gray-500 mt-2">Loading details...</div>
          </div>
          <div v-else-if="userDetails[data.userId]">
            <UserAnomalyDetail
              :report="userDetails[data.userId]"
              @repair="handleRepair"
            />
          </div>
        </div>
      </template>
    </DataTable>

    <div v-if="!scanComplete && !scanning" class="text-center py-12 text-gray-500">
      <i class="pi pi-search text-4xl mb-3" />
      <div>Click "Scan Users" to detect anomalies in the level system</div>
    </div>

    <!-- Bulk Repair Dialog -->
    <Dialog
      header="Bulk Repair"
      v-model:visible="showBulkRepairDialog"
      modal
      :style="{ width: '500px' }"
    >
      <div class="mb-4 text-sm text-gray-600">
        Select which repair operations to run on all anomalous users:
      </div>
      <div class="space-y-3">
        <div v-for="opt in bulkRepairOptions" :key="opt.value" class="flex items-center gap-2">
          <input
            type="checkbox"
            :id="'bulk_' + opt.value"
            v-model="selectedBulkRepairs"
            :value="opt.value"
            class="rounded"
          />
          <label :for="'bulk_' + opt.value" class="text-sm">{{ opt.label }}</label>
        </div>
      </div>
      <template #footer>
        <Button
          label="Cancel"
          severity="secondary"
          @click="showBulkRepairDialog = false"
        />
        <Button
          label="Run Bulk Repair"
          icon="pi pi-wrench"
          severity="warning"
          @click="runBulkRepair"
          :loading="bulkRepairing"
          :disabled="selectedBulkRepairs.length === 0"
        />
      </template>
    </Dialog>

    <!-- Mark Completed Comment Dialog -->
    <Dialog
      header="Mark Level as Completed"
      v-model:visible="markCompletedDialog.visible"
      modal
      :style="{ width: '450px' }"
    >
      <div class="mb-3 text-sm text-gray-600">
        Level {{ markCompletedDialog.levelNumber }} for user {{ markCompletedDialog.userId }}
      </div>
      <div class="mb-2">
        <label class="block text-sm font-medium text-gray-700 mb-1">Admin Comment *</label>
        <textarea
          v-model="markCompletedDialog.comment"
          rows="3"
          class="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
          placeholder="Reason for marking this level as completed..."
        />
      </div>
      <template #footer>
        <Button label="Cancel" severity="secondary" @click="markCompletedDialog.visible = false" />
        <Button
          label="Confirm"
          icon="pi pi-check"
          severity="success"
          @click="confirmMarkCompleted"
          :disabled="!markCompletedDialog.comment.trim()"
          :loading="repairing"
        />
      </template>
    </Dialog>
  </Dialog>
</template>

<script>
import Dialog from 'primevue/dialog';
import Button from 'primevue/button';
import DataTable from 'primevue/datatable';
import Column from 'primevue/column';
import ToggleSwitch from 'primevue/toggleswitch';
import levelSystemService from '@/services/levelSystemService';
import UserAnomalyDetail from './UserAnomalyDetail.vue';

const ANOMALY_TYPE_LABELS = {
  CURRENT_LEVEL_MISMATCH: 'Level Mismatch',
  GOAL_MISMATCH: 'Goal Mismatch',
  XP_MISMATCH: 'XP Mismatch',
  COMPLETION_MISMATCH: 'Completion Mismatch',
  MISSING_LEVEL_PROGRESS: 'Missing Levels',
  LEGACY_DATA_MISMATCH: 'Legacy Mismatch',
  GOAL_PROGRESS_BEHIND: 'Progress Behind',
};

export default {
  name: 'AnomaliesDetector',
  components: { Dialog, Button, DataTable, Column, ToggleSwitch, UserAnomalyDetail },
  props: {
    visible: { type: Boolean, default: false },
  },
  emits: ['close', 'update:visible'],
  data() {
    return {
      scanning: false,
      scanComplete: false,
      exporting: false,
      repairing: false,
      bulkRepairing: false,
      totalScanned: 0,
      healthyCount: 0,
      criticalCount: 0,
      anomalousCount: 0,
      hintCount: 0,
      users: [],
      expandedRows: [],
      userDetails: {},
      loadingDetails: {},
      showOnlyAnomalous: true,
      showBulkRepairDialog: false,
      selectedBulkRepairs: [],
      bulkRepairOptions: [
        { label: 'Sync Goals (fix goal mismatches)', value: 'sync_goals' },
        { label: 'Recalculate (fix XP and completion)', value: 'recalculate' },
        { label: 'Assign Missing Levels', value: 'assign_missing_levels' },
        { label: 'Reset Current Level', value: 'reset_current_level' },
      ],
      markCompletedDialog: {
        visible: false,
        userId: '',
        levelNumber: 0,
        comment: '',
      },
    };
  },
  computed: {
    dialogVisible: {
      get() { return this.visible; },
      set(val) { this.$emit('update:visible', val); },
    },
    filteredUsers() {
      if (this.showOnlyAnomalous) {
        return this.users.filter((u) => u.status !== 'healthy');
      }
      return this.users;
    },
  },
  methods: {
    formatAnomalyType(type) {
      return ANOMALY_TYPE_LABELS[type] || type;
    },

    async runScan() {
      this.scanning = true;
      this.scanComplete = false;
      try {
        const { data } = await levelSystemService.scanAnomalies();
        this.totalScanned = data.totalScanned;
        this.healthyCount = data.healthyCount;
        this.criticalCount = data.criticalCount || 0;
        this.anomalousCount = data.anomalousCount;
        this.hintCount = data.hintCount || 0;
        this.users = data.users;
        this.scanComplete = true;
        this.expandedRows = [];
        this.userDetails = {};
      } catch (error) {
        console.error('Scan failed:', error);
        this.$toast.add({
          severity: 'error',
          summary: 'Scan Failed',
          detail: error?.response?.data?.message || 'Failed to scan users',
          life: 5000,
        });
      } finally {
        this.scanning = false;
      }
    },

    async exportReport() {
      this.exporting = true;
      try {
        const response = await levelSystemService.exportAnomaliesReport();
        const blob = new Blob([response.data], { type: 'text/csv' });
        const url = URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        link.download = `anomalies-report-${new Date().toISOString().slice(0, 10)}.csv`;
        link.click();
        URL.revokeObjectURL(url);
      } catch (error) {
        console.error('Export failed:', error);
        this.$toast.add({
          severity: 'error',
          summary: 'Export Failed',
          detail: 'Failed to export anomalies report',
          life: 5000,
        });
      } finally {
        this.exporting = false;
      }
    },

    async onRowExpand(event) {
      const userId = event.data.userId;
      if (this.userDetails[userId]) return;
      this.loadingDetails = { ...this.loadingDetails, [userId]: true };
      try {
        const { data } = await levelSystemService.getUserAnomalyDetails(userId);
        this.userDetails = { ...this.userDetails, [userId]: data };
      } catch (error) {
        console.error('Failed to load user details:', error);
        this.$toast.add({
          severity: 'error',
          summary: 'Error',
          detail: 'Failed to load user anomaly details',
          life: 5000,
        });
      } finally {
        this.loadingDetails = { ...this.loadingDetails, [userId]: false };
      }
    },

    async handleRepair({ action, userId, levelNumber, goalId }) {
      if (action === 'mark_completed') {
        this.markCompletedDialog = {
          visible: true,
          userId,
          levelNumber,
          comment: '',
        };
        return;
      }

      this.repairing = true;
      try {
        let result;
        switch (action) {
          case 'sync_goals':
            result = await levelSystemService.repairSyncGoals(userId, levelNumber);
            break;
          case 'delete_goal':
            result = await levelSystemService.repairDeleteGoal(userId, levelNumber, goalId);
            break;
          case 'recalculate':
            result = await levelSystemService.repairRecalculate(userId);
            break;
          case 'assign_missing_levels':
            result = await levelSystemService.repairAssignMissingLevels(userId);
            break;
          case 'reset_current_level':
            result = await levelSystemService.repairResetCurrentLevel(userId);
            break;
          case 'fix_goal_progress':
            result = await levelSystemService.repairFixGoalProgress(userId, levelNumber, goalId);
            break;
          default:
            return;
        }
        this.$toast.add({
          severity: 'success',
          summary: 'Repair Successful',
          detail: result?.data?.message || 'Repair action completed',
          life: 4000,
        });
        await this.refreshUserDetails(userId);
      } catch (error) {
        console.error('Repair failed:', error);
        this.$toast.add({
          severity: 'error',
          summary: 'Repair Failed',
          detail: error?.response?.data?.message || 'Repair action failed',
          life: 5000,
        });
      } finally {
        this.repairing = false;
      }
    },

    async confirmMarkCompleted() {
      const { userId, levelNumber, comment } = this.markCompletedDialog;
      this.repairing = true;
      try {
        const result = await levelSystemService.repairMarkCompleted(userId, levelNumber, comment);
        this.$toast.add({
          severity: 'success',
          summary: 'Repair Successful',
          detail: result?.data?.message || 'Level marked as completed',
          life: 4000,
        });
        this.markCompletedDialog.visible = false;
        await this.refreshUserDetails(userId);
      } catch (error) {
        console.error('Mark completed failed:', error);
        this.$toast.add({
          severity: 'error',
          summary: 'Repair Failed',
          detail: error?.response?.data?.message || 'Failed to mark level as completed',
          life: 5000,
        });
      } finally {
        this.repairing = false;
      }
    },

    async runBulkRepair() {
      this.bulkRepairing = true;
      try {
        const result = await levelSystemService.repairBulkAll(this.selectedBulkRepairs);
        const d = result.data;
        this.$toast.add({
          severity: d.errors > 0 ? 'warn' : 'success',
          summary: 'Bulk Repair Complete',
          detail: `Processed ${d.totalProcessed} users, repaired ${d.totalRepaired}, errors: ${d.errors}`,
          life: 6000,
        });
        this.showBulkRepairDialog = false;
        await this.runScan();
      } catch (error) {
        console.error('Bulk repair failed:', error);
        this.$toast.add({
          severity: 'error',
          summary: 'Bulk Repair Failed',
          detail: error?.response?.data?.message || 'Bulk repair failed',
          life: 5000,
        });
      } finally {
        this.bulkRepairing = false;
      }
    },

    async refreshUserDetails(userId) {
      try {
        const { data } = await levelSystemService.getUserAnomalyDetails(userId);
        this.userDetails = { ...this.userDetails, [userId]: data };
        const userIndex = this.users.findIndex((u) => u.userId === userId);
        if (userIndex >= 0) {
          const criticals = (data.anomalies || []).filter((a) => a.severity === 'critical');
          const errors = (data.anomalies || []).filter((a) => a.severity === 'error');
          const hints = (data.anomalies || []).filter((a) => a.severity === 'hint');
          const anomalyTypes = [...new Set(data.anomalies.map((a) => a.type))];
          const status = criticals.length > 0
            ? 'critical'
            : errors.length > 0
              ? 'anomalous'
              : hints.length > 0 ? 'hint' : 'healthy';
          this.users[userIndex] = {
            ...this.users[userIndex],
            status,
            anomalyCount: errors.length + criticals.length,
            criticalCount: criticals.length,
            hintCount: hints.length,
            anomalyTypes,
          };
          this.criticalCount = this.users.filter((u) => u.status === 'critical').length;
          this.anomalousCount = this.users.filter((u) => u.status === 'anomalous').length;
          this.hintCount = this.users.filter((u) => u.status === 'hint').length;
          this.healthyCount = this.totalScanned - this.criticalCount - this.anomalousCount - this.hintCount;
        }
      } catch (error) {
        console.error('Failed to refresh user details:', error);
      }
    },
  },
};
</script>

<style scoped>
.anomaly-critical-card {
  background: linear-gradient(135deg, #fef2f2, #fce7f3);
  border: 2px solid #ef4444;
  color: #991b1b;
  animation: critical-pulse 2s ease-in-out infinite;
  box-shadow: 0 0 12px rgba(239, 68, 68, 0.3);
}

.anomaly-critical-badge {
  background: linear-gradient(135deg, #dc2626, #b91c1c);
  color: #fff;
  animation: badge-pulse 1.5s ease-in-out infinite;
  box-shadow: 0 0 8px rgba(239, 68, 68, 0.5);
}

.anomaly-critical-icon {
  animation: icon-spin 2s ease-in-out infinite;
}

@keyframes critical-pulse {
  0%, 100% { box-shadow: 0 0 12px rgba(239, 68, 68, 0.3); border-color: #ef4444; }
  50% { box-shadow: 0 0 24px rgba(239, 68, 68, 0.6); border-color: #dc2626; }
}

@keyframes badge-pulse {
  0%, 100% { box-shadow: 0 0 6px rgba(239, 68, 68, 0.4); transform: scale(1); }
  50% { box-shadow: 0 0 14px rgba(239, 68, 68, 0.7); transform: scale(1.05); }
}

@keyframes icon-spin {
  0%, 100% { transform: rotate(0deg); }
  25% { transform: rotate(15deg); }
  75% { transform: rotate(-15deg); }
}
</style>
